import type { FunnelStep } from "../api";
import { count, delta, toneColor } from "../format";

/**
 * The funnel, drawn as a funnel: centered segments whose width tracks volume,
 * trapezoid connectors carrying the step-to-step conversion, and — the "vs
 * change" ask — a dashed outline behind each segment showing the previous
 * window's width, with a per-step delta in the right gutter.
 *
 * Width is compressed (pow 0.42, matching the old ladder) so a 400k → 200
 * funnel stays readable end to end. A zero step still draws — a missing rung
 * is the finding, not a rendering bug.
 */

const W = 960;
const ROW = 66;
const BAR = 34;
const LABEL_X = 178;
const FX0 = 200;
const FX1 = 794;
const VALUE_X = 952;
const CX = (FX0 + FX1) / 2;
const MAX_HW = (FX1 - FX0) / 2;

function halfWidth(v: number, max: number): number {
  if (v <= 0) return 0;
  return Math.max(Math.pow(v / max, 0.42), 0.045) * MAX_HW;
}

function segColor(i: number, n: number): string {
  const t = n <= 1 ? 1 : i / (n - 1);
  if (t >= 0.99) return "var(--pos)";
  if (t >= 0.55) return "var(--text-4)";
  return "var(--border-hover)";
}

export function FunnelViz({
  steps,
  current,
  previous,
  selected,
  onSelect,
}: {
  steps: FunnelStep[];
  current: Record<string, number>;
  previous: Record<string, number> | null;
  selected: string | null;
  onSelect: (stage: string) => void;
}) {
  const vols = steps.map((s) => current[s.stage] ?? 0);
  const prevVols = steps.map((s) => previous?.[s.stage] ?? 0);
  const max = Math.max(...vols, ...(previous ? prevVols : [0]), 1);
  const H = steps.length * ROW - (ROW - BAR) + 8;

  if (vols.every((v) => v === 0) && prevVols.every((v) => v === 0)) {
    return (
      <div style={{ padding: "44px 0", textAlign: "center", color: "var(--text-4)" }}>
        <div style={{ fontSize: 14.5, fontWeight: 500, color: "var(--text-2)" }}>No funnel events in this window.</div>
        <div style={{ fontSize: 12.5, marginTop: 6 }}>
          Connect a source in Integrations, or widen the range.
        </div>
      </div>
    );
  }

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      style={{ width: "100%", height: "auto", display: "block", overflow: "visible" }}
      role="img"
      aria-label="Funnel by step, current window against the previous one."
    >
      {steps.map((s, i) => {
        const y = i * ROW + 4;
        const v = vols[i];
        const hw = halfWidth(v, max);
        const phw = previous ? halfWidth(prevVols[i], max) : 0;
        const isSel = selected === s.stage;
        const d = previous ? delta(v, prevVols[i], "up") : null;

        return (
          <g key={s.stage} onClick={() => onSelect(s.stage)} style={{ cursor: "pointer" }}>
            {/* connector to the next step, with its conversion */}
            {i < steps.length - 1 && (
              <>
                <polygon
                  points={[
                    `${CX - hw},${y + BAR}`, `${CX + hw},${y + BAR}`,
                    `${CX + halfWidth(vols[i + 1], max)},${y + ROW}`,
                    `${CX - halfWidth(vols[i + 1], max)},${y + ROW}`,
                  ].join(" ")}
                  fill="var(--surface-inset)"
                />
                <text x={CX} y={y + BAR + (ROW - BAR) / 2 + 4} textAnchor="middle" className="num"
                      fontSize="11" fill={convTone(v, vols[i + 1])}>
                  {v > 0 ? `${((vols[i + 1] / v) * 100).toFixed(1)}% ↓` : "—"}
                </text>
              </>
            )}

            {/* previous window, as a dashed outline behind the segment */}
            {previous && phw > 0 && (
              <rect x={CX - phw} y={y - 2.5} width={phw * 2} height={BAR + 5} rx={7}
                    fill="none" stroke="var(--border-hover)" strokeDasharray="4 3" strokeWidth="1" />
            )}

            {/* the segment itself */}
            {hw > 0 ? (
              <rect x={CX - hw} y={y} width={hw * 2} height={BAR} rx={5}
                    fill={segColor(i, steps.length)} opacity={isSel ? 1 : 0.88}
                    stroke={isSel ? "var(--keeper)" : "none"} strokeWidth={isSel ? 1.6 : 0} />
            ) : (
              <line x1={CX - 14} y1={y + BAR / 2} x2={CX + 14} y2={y + BAR / 2}
                    stroke="var(--border-strong)" strokeWidth="2" strokeDasharray="3 3" />
            )}

            {/* left gutter: the step's name */}
            <text x={LABEL_X} y={y + BAR / 2 - 2} textAnchor="end" fontSize="13"
                  fontWeight={isSel ? 600 : 500} fill={isSel ? "var(--text)" : "var(--text-2)"}>
              {s.label}
            </text>
            <text x={LABEL_X} y={y + BAR / 2 + 13} textAnchor="end" fontSize="10"
                  fill="var(--text-faint)" style={{ textTransform: "uppercase", letterSpacing: "0.07em" }}>
              {s.stage}
            </text>

            {/* right gutter: volume + change vs previous window */}
            <text x={VALUE_X} y={y + BAR / 2 - 2} textAnchor="end" className="num" fontSize="14.5"
                  fontWeight={500} fill={v === 0 ? "var(--text-faint)" : "var(--text)"}>
              {count(v)}
            </text>
            <text x={VALUE_X} y={y + BAR / 2 + 13} textAnchor="end" className="num" fontSize="11"
                  fill={d ? toneColor(d.tone) : "var(--text-faint)"}>
              {d ? d.label : previous ? "—" : ""}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function convTone(from: number, to: number): string {
  if (from <= 0) return "var(--text-faint)";
  const conv = to / from;
  return conv < 0.15 ? "var(--warn)" : "var(--text-faint)";
}
